import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'jobConfigCache_v1';
const POLL_INTERVAL_MS = 30_000;
const BROADCAST_CHANNEL = 'jobconfig-sync-v1';

// Tailwind class lookup (for CSS contexts: badges, dots).
const COLOR_CLASSES = {
  slate: 'bg-slate-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  yellow: 'bg-yellow-500',
  lime: 'bg-lime-500',
  green: 'bg-green-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  sky: 'bg-sky-500',
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
  purple: 'bg-purple-500',
  pink: 'bg-pink-500',
  rose: 'bg-rose-500',
};

// Hex lookup for non-CSS contexts (Gantt bars, charts). Mirrors Tailwind's
// 500-shade palette so colors stay visually identical across the app.
const COLOR_HEX = {
  slate: '#64748B',
  red: '#EF4444',
  orange: '#F97316',
  amber: '#F59E0B',
  yellow: '#EAB308',
  lime: '#84CC16',
  green: '#22C55E',
  emerald: '#10B981',
  teal: '#14B8A6',
  cyan: '#06B6D4',
  sky: '#0EA5E9',
  blue: '#3B82F6',
  indigo: '#6366F1',
  violet: '#8B5CF6',
  purple: '#A855F7',
  pink: '#EC4899',
  rose: '#F43F5E',
};

export const statusColorClass = (color) => COLOR_CLASSES[color] || COLOR_CLASSES.slate;
export const statusHexColor = (color) =>
  (typeof color === 'string' && color.startsWith('#') ? color : COLOR_HEX[color] || COLOR_HEX.slate);

const EMPTY_CONFIG = {
  statuses: [],
  types: [],
  is_silver_tier: false,
  is_gold_tier: false,
  allowed_colors: [],
};

function loadCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.statuses)) return parsed;
  } catch {
    // ignore
  }
  return null;
}

const JobConfigContext = createContext(null);

export function JobConfigProvider({ children }) {
  const { user } = useAuth();
  // Hydrate from localStorage synchronously so the first paint shows real
  // colors instead of grey fallbacks.
  const cached = loadCached();
  const [config, setConfig] = useState(cached || EMPTY_CONFIG);
  // If we hydrated from cache we already have "real" data — show it as
  // non-loading immediately, then revalidate in the background.
  const [loading, setLoading] = useState(!cached);
  const lastUserKeyRef = useRef(null);
  // Track the current user via a ref so the memoised fetchConfig can read
  // it without re-creating on every login.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const fetchConfig = useCallback(async ({ broadcast = false } = {}) => {
    try {
      const res = await axios.get(`${API}/job-config`);
      setConfig(res.data);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(res.data));
      } catch {
        // localStorage can fail in private-browsing mode; ignore.
      }
      if (broadcast && typeof BroadcastChannel !== 'undefined') {
        try {
          const ch = new BroadcastChannel(BROADCAST_CHANNEL);
          ch.postMessage({
            type: 'config-updated',
            org: userRef.current?.organization_id || null,
            version: res.data?.config_version ?? 0,
          });
          ch.close();
        } catch {
          // BroadcastChannel can be flaky in some browsers; ignore.
        }
      }
    } catch {
      // Keep existing (possibly cached) config on failure.
    } finally {
      setLoading(false);
    }
  }, []);

  // Public refresh: callers (e.g., JobConfigSettings after a mutation) get a
  // fetch *and* a broadcast to peer tabs.
  const refresh = useCallback(() => fetchConfig({ broadcast: true }), [fetchConfig]);

  useEffect(() => {
    // Re-fetch whenever the auth user changes (login, org switch, logout).
    // On logout (user=null) we clear the cache so the next user doesn't see
    // someone else's statuses.
    const key = user ? `${user.organization_id}:${user.id}` : null;
    if (key === lastUserKeyRef.current) return;
    lastUserKeyRef.current = key;
    if (!user) {
      try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
      setConfig(EMPTY_CONFIG);
      setLoading(false);
      return;
    }
    fetchConfig();
  }, [user, fetchConfig]);

  // Cross-tab sync via BroadcastChannel: when *this* tab calls refresh()
  // after a Settings mutation, every other tab in the same browser refreshes
  // immediately. Falls back silently if the API isn't available (older
  // Safari/iOS).
  useEffect(() => {
    if (!user || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(BROADCAST_CHANNEL);
    const handler = (e) => {
      if (e?.data?.type === 'config-updated' && e.data.org === user.organization_id) {
        fetchConfig();
      }
    };
    ch.addEventListener('message', handler);
    return () => {
      ch.removeEventListener('message', handler);
      ch.close();
    };
  }, [user, fetchConfig]);

  // Cross-user / cross-device sync via lightweight version polling. Every
  // 30 seconds we ask the server for the org's config_version. If it has
  // bumped past what we last saw, we refetch the full config. This is how
  // changes propagate from another manager's browser to ours.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/job-config/version`);
        const remote = res.data?.version ?? 0;
        const local = config.config_version ?? 0;
        if (remote > local) {
          await fetchConfig();
        }
      } catch {
        // Network blips are fine — we'll try again next tick.
      }
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, config.config_version, fetchConfig]);

  const value = useMemo(() => {
    const statuses = config.statuses || [];
    const types = config.types || [];
    const visibleStatuses = statuses.filter((s) => !s.hidden);
    const visibleTypes = types.filter((t) => !t.hidden);

    const labelForStatus = (val) => {
      const s = statuses.find((x) => x.value === val);
      return s
        ? s.label
        : (val || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };
    const labelForType = (val) => {
      const t = types.find((x) => x.value === val);
      return t
        ? t.label
        : (val || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };
    const colorForStatus = (val) => {
      const s = statuses.find((x) => x.value === val);
      return s?.color || 'slate';
    };
    const hexForStatus = (val) => statusHexColor(colorForStatus(val));
    const knownStatus = (val) => statuses.some((x) => x.value === val);

    return {
      ...config,
      loading,
      visibleStatuses,
      visibleTypes,
      labelForStatus,
      labelForType,
      colorForStatus,
      hexForStatus,
      knownStatus,
      refresh,
    };
  }, [config, loading, refresh]);

  return <JobConfigContext.Provider value={value}>{children}</JobConfigContext.Provider>;
}

export default function useJobConfig() {
  const ctx = useContext(JobConfigContext);
  if (!ctx) {
    // Defensive fallback so any component used outside the provider still
    // gets a usable (empty) shape rather than crashing.
    return {
      ...EMPTY_CONFIG,
      loading: true,
      visibleStatuses: [],
      visibleTypes: [],
      labelForStatus: (v) => v || '',
      labelForType: (v) => v || '',
      colorForStatus: () => 'slate',
      hexForStatus: () => COLOR_HEX.slate,
      knownStatus: () => false,
      refresh: () => {},
    };
  }
  return ctx;
}
